import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Grid,
  Popover,
  Autocomplete,
  Divider,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { useNavigate } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import { getItemApi } from "../services/item.service";
import { addDynamicTemplateApi } from "../services/dynamic-template.service";
import MasterDynamicTemplateSettings from "./MasterDynamicTemplateSettings";
import MasterDynamicTemplateSettingsNumericField from "./MasterDynamicTemplateSettingsNumericField";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const SAMPLE_CONTAINER_WISE_DETAIL_LABEL = [
  {
    id: 1,
    label: "Sample Type",
    value: "sampleType",
  },
  {
    id: 2,
    label: "Container Type",
    value: "containerType",
  },
  {
    id: 3,
    label: "",
    value: "",
  },
];

export default function MasterAddDynamicTemplate() {
  const navigate = useNavigate();

  const initialState = {
    name: "",
    code: "",
    formType: "",
    templateType: "",
    item: [],
    sampleContainers: [],
    dynamicForm: [],
  };

  const [formData, setFormData] = useState(initialState);
  const [sampleContainers, setSampleContainers] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for dynamicForm, initially empty
  const [dynamicForm, setDynamicForm] = useState(
    initialState?.dynamicForm || []
  );
  // State for popover (plus icon)
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [addingSubRowTo, setAddingSubRowTo] = useState(null);
  const [masterTemplateSettingsModalOpen, setMasterTemplateSettingsModalOpen] =
    useState(false);
  const [subRowSettingsModalOpen, setSubRowSettingsModalOpen] = useState(false);
  const [selectedSubRowType, setSelectedSubRowType] = useState(null);
  const [editingRow, setEditingRow] = useState(null);

  const { dataList: itemList } = useGetApi({
    apiFunction: getItemApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "items",
  });

  const handleMasterTemplateSettingsModalClose = () => {
    setMasterTemplateSettingsModalOpen(false);
  };
  const handleSubRowSettingsModalOpen = () => {
    setSubRowSettingsModalOpen(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    setFormData((prevState) => {
      return {
        ...prevState,
        [name]: parsedValue,
      };
    });
  };

  const handleSampleContainerChange = (event, rowIndex, currentState) => {
    const { value, name } = event.target;

    // Update state without modifying the original array
    currentState((prevState) =>
      prevState.map((row, i) =>
        i === rowIndex ? { ...row, [name]: value } : row
      )
    );
  };

  // handle add item
  const handleAddTableRow = (currentElement, currentState, newRowData) => {
    currentState([...currentElement, newRowData]);
  };

  // handle remove item
  const handleRemoveTableRow = (rowIndex, currentElement, currentState) => {
    currentState(currentElement?.filter((_, index) => index !== rowIndex));
  };

  // Add a new main row
  const addMainRow = () => {
    setDynamicForm([
      ...dynamicForm,
      {
        displayText: "",
        elementName: "",
        code: "",
        nameFirst: true,
        valueFirst: false,
        bold: true,
        subForm: true,
        displayType: "",
        separator: "",
        allies: "",
        fields: [],
      },
    ]);
  };

  // Remove a main row
  const removeMainRow = (mainIndex) => {
    setDynamicForm(dynamicForm?.filter((_, i) => i !== mainIndex));
  };

  // Remove a sub-row
  const removeSubRow = (mainIndex, subIndex) => {
    setDynamicForm(
      dynamicForm?.map((row, i) =>
        i === mainIndex
          ? { ...row, fields: row?.fields?.filter((_, j) => j !== subIndex) }
          : row
      )
    );
  };

  // Copy a main row and its sub-rows
  const copyMainRow = (mainIndex) => {
    const rowToCopy = dynamicForm[mainIndex];
    const copiedRow = { ...rowToCopy, fields: [...rowToCopy?.fields] };
    setDynamicForm([...dynamicForm, copiedRow]);
  };

  // Handle plus icon click (open popover)
  const handlePlusClick = (mainIndex, event) => {
    setAddingSubRowTo(mainIndex);
    setPopoverAnchor(event.currentTarget);
    setEditingRow(null);
  };

  // Close popover
  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setAddingSubRowTo(null);
  };

  // Handle type selection from popover
  const handleTypeSelect = (type) => {
    setSelectedSubRowType(type);
    setSubRowSettingsModalOpen(true);
    setPopoverAnchor(null);
  };

  // Handle settings icon click (open dialog)
  const handleSettingsClick = (rowData, isSubRow, mainIndex, subIndex) => {
    setEditingRow({ rowData, isSubRow, mainIndex, subIndex });
    if (isSubRow) {
      setSubRowSettingsModalOpen(true);
    } else {
      setMasterTemplateSettingsModalOpen(true);
    }
  };

  const handleSave = async () => {
    const payload = {
      ...formData,
      item: formData?.item?.map((option) => option?._id),
      sampleContainers,
      dynamicForm,
    };

    setLoading(true);
    const response = await addDynamicTemplateApi(payload);
    setLoading(false);

    if (response?.code === 200 || response?.code === 201) {
      toast.success(response?.message || `Dynamic template added successfully`);
      navigate("/master/dynamic-template");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
        <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: '#5D6A82', 
              textTransform: 'none', 
              fontSize: '16px',
              fontWeight: 'bold',
              padding: 0,
              transition: 'none !important',
              mt:2
            }}
            >
              Back
          </Button>
          {/* Search and Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={{ color: "inherit" }}>
              Add Dynamic Template
            </Typography>
            {/* <IconButton onClick={() => navigate(-1)}>
              <CloseIcon />
            </IconButton> */}
          </Box>
          {/* Edit Dynamic Form Template */}
          <Box
            sx={{
              backgroundColor: "rgba(240, 247, 252, 1)",
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Typography sx={{ pb: "15px" }}>
              Edit Dynamic Form Template
            </Typography>
            <Grid container spacing={2}>
              {/* name*/}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData?.name || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* code */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Code"
                  name="code"
                  value={formData?.code || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* formType */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Form Type"
                  name="formType"
                  value={formData?.formType || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* templateType */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Template Type"
                  name="templateType"
                  value={formData?.templateType || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* item/package */}
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  limitTags={3}
                  options={itemList || []}
                  getOptionLabel={(option) => option?.itemName || ""}
                  renderInput={(params) => (
                    <TextField {...params} label="Item/Package" />
                  )}
                  value={formData?.item || null}
                  onChange={(_, newValue) =>
                    handleChange({
                      target: { name: "item", value: newValue },
                    })
                  }
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Sample container wise detail */}
          <Box
            sx={{
              border: `1px solid #CCD4DA`,
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: "20px",
              }}
            >
              <Typography>Sample Container Wise Detail</Typography>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead
                  sx={{
                    bgcolor: "#EAEAEA",
                  }}
                >
                  <TableRow>
                    {SAMPLE_CONTAINER_WISE_DETAIL_LABEL?.map((option) => (
                      <TableCell
                        key={option?.id}
                        sx={{
                          textAlign: option?.align || "left",
                          color: "#5D6A82",
                        }}
                      >
                        {option?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sampleContainers.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {SAMPLE_CONTAINER_WISE_DETAIL_LABEL?.map(
                        (column, columnIndex) => (
                          <TableCell
                            key={`${rowIndex}${columnIndex}`}
                            sx={{ color: "#5D6A82" }}
                          >
                            {columnIndex ===
                            SAMPLE_CONTAINER_WISE_DETAIL_LABEL?.length - 1 ? (
                              <CloseIcon
                                sx={{ cursor: "pointer", fontSize: "18px" }}
                                onClick={() =>
                                  handleRemoveTableRow(
                                    rowIndex,
                                    sampleContainers,
                                    setSampleContainers
                                  )
                                }
                              />
                            ) : (
                              <TextField
                                label={column?.label}
                                required
                                name={column?.value}
                                value={row[column?.value] || ""}
                                onChange={(e) =>
                                  handleSampleContainerChange(
                                    e,
                                    rowIndex,
                                    setSampleContainers
                                  )
                                }
                              />
                            )}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      colSpan={SAMPLE_CONTAINER_WISE_DETAIL_LABEL?.length}
                      sx={{ color: "#92A6CC", cursor: "pointer" }}
                      onClick={() =>
                        handleAddTableRow(
                          sampleContainers,
                          setSampleContainers,
                          {
                            sampleType: "",
                            containerType: "",
                          }
                        )
                      }
                    >
                      + Add item
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Dynamic form */}
          <Box
            sx={{
              border: `1px solid #CCD4DA`,
              borderRadius: "12px",
              mb: "30px",
              p: "20px",
            }}
          >
            <Typography variant="h6" sx={{ mb: "20px" }}>
              Dynamic Form
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead
                  sx={{
                    bgcolor: "#EAEAEA",
                  }}
                >
                  <TableRow>
                    <TableCell colSpan={3}>Dynamic Form Element</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dynamicForm?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No items added yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    dynamicForm?.map((mainRow, mainIndex) => (
                      <React.Fragment key={mainIndex}>
                        {/* Main Row */}
                        <TableRow>
                          <TableCell sx={{ width: "20%" }}>Label</TableCell>
                          <TableCell sx={{ width: "50%" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography>Display Text: </Typography>
                              <Typography>
                                {mainRow?.displayText || ""}
                              </Typography>
                              <Divider
                                orientation="vertical"
                                sx={{
                                  height: "20px",
                                  width: "1px",
                                  mx: 1,
                                  bgcolor: "grey",
                                }}
                              />
                              <Typography>Code: </Typography>
                              <Typography>{mainRow?.code || ""}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: "30%" }}>
                            <IconButton
                              onClick={() =>
                                handleSettingsClick(mainRow, false, mainIndex)
                              }
                            >
                              <SettingsOutlinedIcon />
                            </IconButton>
                            {mainRow?.fields?.length === 0 && (
                              <IconButton
                                onClick={(e) => handlePlusClick(mainIndex, e)}
                              >
                                <AddOutlinedIcon />
                              </IconButton>
                            )}
                            <IconButton onClick={() => copyMainRow(mainIndex)}>
                              <ContentCopyOutlinedIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => removeMainRow(mainIndex)}
                            >
                              <CloseIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {/* Sub-Rows */}
                        {mainRow?.fields?.map((subRow, subIndex) => (
                          <TableRow key={subIndex}>
                            <TableCell sx={{ pl: 4 }}>
                              {subRow?.type || "Sub-Row"}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography>Display Text: </Typography>
                                <Typography>
                                  {subRow?.displayText || ""}
                                </Typography>
                                <Divider
                                  orientation="vertical"
                                  sx={{
                                    height: "20px",
                                    width: "1px",
                                    mx: 1,
                                    bgcolor: "grey",
                                  }}
                                />
                                <Typography>Code: </Typography>
                                <Typography>{subRow?.code || ""}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() =>
                                  handleSettingsClick(
                                    subRow,
                                    true,
                                    mainIndex,
                                    subIndex
                                  )
                                }
                              >
                                <SettingsOutlinedIcon />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  removeSubRow(mainIndex, subIndex)
                                }
                              >
                                <CloseIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{ color: "#92A6CC", cursor: "pointer" }}
                      onClick={addMainRow}
                    >
                      + Add item
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* save button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: "20px",
            }}
          >
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={loading}
              onClick={handleSave}
            >
              {loading ? <CircularProgress size={24} /> : `Save`}
            </Button>
          </Box>

          {/* Popover for selecting sub-row type */}
          <Popover
            open={Boolean(popoverAnchor)}
            anchorEl={popoverAnchor}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleTypeSelect("Numeric")}>
              Numeric
            </MenuItem>
            <MenuItem onClick={() => handleTypeSelect("String")}>
              String
            </MenuItem>
          </Popover>

          {/* Main Row */}
          <MasterDynamicTemplateSettings
            open={masterTemplateSettingsModalOpen}
            onClose={handleMasterTemplateSettingsModalClose}
            setDynamicForm={setDynamicForm}
            editingRow={editingRow}
          />

          {/* Sub Row */}
          <MasterDynamicTemplateSettingsNumericField
            open={subRowSettingsModalOpen}
            onClose={handleSubRowSettingsModalOpen}
            selectedSubRowType={selectedSubRowType}
            addingSubRowTo={addingSubRowTo}
            dynamicForm={dynamicForm}
            setDynamicForm={setDynamicForm}
            editingRow={editingRow}
          />
        </Box>
      </Box>
    </Container>
  );
}
